import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import LayoutWrapper from "./components/layout/LayoutWrapper";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import { useAuthContext } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { appRoutes, homeByRole } from "./routes";
import { ToastProvider } from "./components/ui";
import Unauthorized from "./pages/Unauthorized";
import { DarkModeProvider } from "./context/DarkModeContext";
import { useModuleUsageTracking } from "./hooks/useModuleUsageTracking";

/**
 * Usage Tracking Wrapper - Tracks module usage across all routes
 */
const UsageTrackingWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Enable automatic module usage tracking
  useModuleUsageTracking(true);
  return <>{children}</>;
};

const App: React.FC = () => {
  const { user, role } = useAuthContext();
  const homePath = role ? homeByRole[role] || "/entry" : "/entry";
  return (
    <Router>
      <DarkModeProvider>
        <ToastProvider />
        <UsageTrackingWrapper>
          <LayoutWrapper>
            <Routes>
            <Route
              path="/login"
              element={!user ? <LoginPage /> : <Navigate to={homePath} replace />}
            />
            <Route
              path="/register"
              element={!user ? <RegisterPage /> : <Navigate to={homePath} replace />}
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to={homePath} replace />
                </ProtectedRoute>
              }
            />
            {appRoutes.map(r => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  <ProtectedRoute rolesAllowed={r.rolesAllowed}>
                    <Suspense fallback={<div className="p-4">Loading...</div>}>
                      {r.element}
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<Navigate to={user ? homePath : "/login"} replace />} />

          </Routes>
        </LayoutWrapper>
        </UsageTrackingWrapper>
      </DarkModeProvider>
    </Router>
  );
};


export default App;
